import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  Injector,
  OnDestroy,
  OnInit,
  TemplateRef,
  viewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import {
  backNavigation,
  Command,
  CommandStore,
  handleRequestError,
  refresh,
  requestFn,
  Scan,
  scan,
  ScannerStore,
  showRequestErrors,
  SOFTLINE_FEATURE_COMMANDS,
  SOFTLINE_FEATURE_MASTER_DATA,
  SOFTLINE_FEATURE_SCANNER,
  SOFTLINE_FEATURE_TITLE,
  TitleStore,
} from '@softline/application';
import {
  DateService,
  IsoDate,
  RemoteObjectStore,
  SOFTLINE_SERVICE_UUID,
  Store,
  toIsoDate,
} from '@softline/core';
import {
  MessageBarStore,
  modalFn,
  SOFTLINE_FEATURE_MESSAGE_BAR,
  translator,
} from '@softline/ui-core';
import { BehaviorSubject, combineLatest, Observable, shareReplay } from 'rxjs';
import { map } from 'rxjs/operators';
import { DeliveryNoteCardComponent } from '../../components/delivery-note-card/delivery-note-card.component';
import { SegmentedControlComponent } from '../../components/segmented-control/segmented-control.component';
import {
  isPalletEnabled,
  SOFTLINE_CONFIG_LIEFERSCHEIN,
  SOFTLINE_FEATURE_DELIVERY_NOTE,
} from '../../delivery-note.shared';
import { DeliveryNotesStore } from '../../stores';
import { DeliveryNote } from '../../types/delivery-note';

import { DateNavigatorComponent } from '@softline/ui-core/date';
import { InputComponent } from '@softline/ui-core';
import { LoadingSpinnerComponent } from '@softline/ui-core';
import { SortPipe } from '@softline/ui-core';
import { TranslatePipe } from '@softline/ui-core';
import { menuBar, MenuBarButton } from '@softapps/core';
import { PageComponent, PageHeaderComponent } from '@softapps/core/pages';
import { DeliveryNoteService } from '../../services/delivery-note.service';
import { TakeOverRequestBody } from '../../types/take-over-request';
import { Vehicle } from '../../types/vehicle';
import { Stammdaten } from '../../types/stammdaten';
import { SsccSearchResultDialog } from '../sscc-search-result-dialog/sscc-search-result-dialog';
@Component({
  selector: 'soft-overview',
  imports: [
    CommonModule,
    DeliveryNoteCardComponent,
    SegmentedControlComponent,
    DateNavigatorComponent,
    InputComponent,
    LoadingSpinnerComponent,
    SortPipe,
    TranslatePipe,
    PageComponent,
    PageHeaderComponent,
    MenuBarButton,
  ],
  templateUrl: './overview.component.html',
  styleUrls: ['./overview.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OverviewComponent implements OnInit, OnDestroy {
  private readonly store = inject(Store);
  private readonly palletEnabled = isPalletEnabled(
    inject(SOFTLINE_CONFIG_LIEFERSCHEIN, { optional: true })
  );
  private readonly injector = inject(Injector);
  private readonly deliveryNoteService = inject(DeliveryNoteService);
  private readonly uuid = inject<() => string>(SOFTLINE_SERVICE_UUID);
  protected readonly router = inject(Router);
  private readonly uuidLoad: string = this.uuid();
  private readonly searchInput$ = new BehaviorSubject<string>('');
  private readonly filterType$ = new BehaviorSubject<DeliveryNote['type']['type']>(
    'AUSLIEFERUNG'
  );
  private readonly dateService = inject(DateService);

  readonly viewedDate$ = this.store.observe(
    SOFTLINE_FEATURE_DELIVERY_NOTE,
    DeliveryNotesStore.getters.viewedDate
  );

  private t = translator();
  private request = requestFn();
  private readonly modal = modalFn();

  readonly menuBar = viewChild.required('menuBar', {
    read: TemplateRef,
  });

  //TODO: TEST
  readonly viewedDateSignal = toSignal(this.viewedDate$);
  readonly viewedDate = computed(
    () => (this.viewedDateSignal() as IsoDate) ?? this.dateService.now()
  );

  readonly activeSegment$ = this.filterType$.asObservable();

  readonly loading$ = this.store.observe(
    SOFTLINE_FEATURE_DELIVERY_NOTE,
    DeliveryNotesStore.getters.collection.loading
  );

  private readonly stammdaten = this.store.signal<Stammdaten>(
    SOFTLINE_FEATURE_MASTER_DATA,
    RemoteObjectStore.getters.data
  );

  readonly fahrerModule = computed(() => this.stammdaten()?.fahrerModule);

  readonly deliveryNotes$ = this.store.observe(
    SOFTLINE_FEATURE_DELIVERY_NOTE,
    DeliveryNotesStore.getters.collection.all
  );

  readonly filteredNotes$: Observable<DeliveryNote[]> = combineLatest([
    this.deliveryNotes$,
    this.searchInput$,
    this.filterType$,
    this.viewedDate$,
  ]).pipe(
    map(([items, input, activeFilter, date]) => ({
      items: this.filterItemsByType(items, activeFilter),
      input: input?.toLowerCase(),
      date: toIsoDate(new Date(date)), // 'YYYY-MM-DD'
    })),
    map(({ items, input, date }) => {
      if (input?.length < 1) return items?.filter((note) => note.lieferdatum === date);
      else
        return items
          .filter((note) => {
            return (
              note?.adresseBean?.kurzbez?.toLowerCase().includes(input) ||
              note?.adresseBean?.vorname?.toLowerCase().includes(input) ||
              note?.adresseBean?.nachname?.toLowerCase().includes(input) ||
              note?.adresseBean?.strasse?.toLowerCase().includes(input) ||
              note?.adresseBean?.ort?.toLowerCase().includes(input) ||
              note?.belegnummer.toString().toLowerCase().includes(input)
            );
          })
          .filter((note) => note.lieferdatum === date);
    }),
    shareReplay(1)
  );

  readonly noteTypeSegments$ = combineLatest([
    this.deliveryNotes$,
    this.viewedDate$,
  ]).pipe(
    map(([notes, date]) => {
      const notesForDate = notes.filter(
        (note) => note.lieferdatum === toIsoDate(new Date(date)) // 'YYYY-MM-DD'
      );

      const segments: any[] = [];

      if (notesForDate?.filter((o) => o.type.type === 'AUSLIEFERUNG')?.length > 0) {
        segments.push({
          value: 'AUSLIEFERUNG',
          label: '#DELIVERY_NOTE.OVERVIEW.SEGMENTS.LIEFERSCHEINE',
          hasEntries:
            notesForDate?.filter(
              (o) =>
                o.type.type === 'AUSLIEFERUNG' ||
                o.type.type === 'BARVERKAUF' ||
                o.type.type === 'KREDITRECHNUNG'
            )?.length > 0,
        });
      }

      if (notesForDate?.filter((o) => o.type.type === 'MATERIAL')?.length > 0) {
        segments.push({
          value: 'MATERIAL',
          label: '#DELIVERY_NOTE.OVERVIEW.SEGMENTS.MATERIALSCHEINE',
          hasEntries: notesForDate?.filter((o) => o.type.type === 'MATERIAL')?.length > 0,
        });
      }

      if (segments?.length === 1) {
        this.filterType$.next(segments[0].value);
      }

      if (segments?.length === 2) {
        this.filterType$.next('AUSLIEFERUNG');
      }

      return segments;
    })
  );

  constructor() {
    backNavigation(() => '/');
    refresh(async () => await this.refresh());

    if (this.palletEnabled)
      scan(async (scan: Scan) => {
        await this.findDeliveryNoteBySscc(scan.data);
      });

    menuBar({
      key: 'softapps/wws/lieferschein/overview',
      title: this.t('Aktionen'),
      template: this.menuBar,
      align: 'trailing',
      separated: true,
    });
  }

  async openScanDialog(): Promise<void> {
    try {
      await this.store.dispatch(SOFTLINE_FEATURE_SCANNER, ScannerStore.actions.scan, {
        labelType: ['code39', 'ean128', 'code128'],
      });
    } catch (e) {
      handleRequestError(e, this.injector, { show: true });
    }
  }

  async findDeliveryNoteBySscc(sscc: string): Promise<void> {
    const deliveryNotes = await this.deliveryNoteService.findBySscc(sscc);
    if (!deliveryNotes?.length) return;

    const result = (await this.modal({
      component: SsccSearchResultDialog,
      title: `Lieferscheine`,
      subtitle: `Bitte Lieferschein und Fahrzeug auswählen`,
      data: { deliveryNotes },
      dismiss: true,
    })) as { deliveryNote: DeliveryNote; vehicle: Vehicle } | null;

    if (!result) return;

    const takeOverRequest: TakeOverRequestBody = {
      fahrzeug: result.vehicle,
      lieferdatum: result.deliveryNote.lieferdatum,
    };

    const response = await this.request({
      request: () =>
        this.deliveryNoteService.takeOver(result.deliveryNote.id, takeOverRequest),
      options: {
        type: 'saving',
        savingMessage: computed(() => 'Lieferschein wird übernommen...'),
      },
    });

    if (response === null) return;

    await this.router.navigate(['lieferscheine', result.deliveryNote.id]);
  }

  async ngOnInit(): Promise<void> {
    this.store.commit(
      SOFTLINE_FEATURE_TITLE,
      TitleStore.mutations.setTitle,
      'Auslieferungen'
    );
    this.store.commit(SOFTLINE_FEATURE_COMMANDS, CommandStore.mutations.addSet, {
      name: OverviewComponent,
      commands: this.createCommands(),
    });
    try {
      await this.store.dispatch(
        SOFTLINE_FEATURE_DELIVERY_NOTE,
        DeliveryNotesStore.actions.collection.loadMany,
        {
          clear: true,
          token: this.uuidLoad,
        }
      );
    } catch (e: unknown) {
      showRequestErrors(this.store, e);
    }
  }

  ngOnDestroy(): void {
    this.store.commit(SOFTLINE_FEATURE_TITLE, TitleStore.mutations.setTitle, '');
    this.store.commit(
      SOFTLINE_FEATURE_COMMANDS,
      CommandStore.mutations.removeSet,
      OverviewComponent
    );
  }

  async refresh(): Promise<void> {
    try {
      await this.store.dispatch(
        SOFTLINE_FEATURE_DELIVERY_NOTE,
        DeliveryNotesStore.actions.collection.loadMany,
        {
          clear: true,
        }
      );
    } catch (e: unknown) {
      showRequestErrors(this.store, e);
    }
  }

  protected createCommands(): Command[] {
    return [
      {
        name: 'Scan',
        class:
          'menu top-menu top-menu-right flex self-center justify-center items-center',
        icon: 'fa-regular fa-barcode-scan',
        execute: async () => {
          try {
            const scan = await this.store.dispatch(
              SOFTLINE_FEATURE_SCANNER,
              ScannerStore.actions.scan,
              { labelType: ['code39'] }
            );

            if (!scan || !scan?.data) return;

            const lieferschein = await this.store.dispatch(
              SOFTLINE_FEATURE_DELIVERY_NOTE,
              DeliveryNotesStore.actions.getLieferscheinForScan,
              { labelType: scan?.labelType, code: scan.data }
            );

            if (!lieferschein) return;

            await this.store.dispatch(
              SOFTLINE_FEATURE_MESSAGE_BAR,
              MessageBarStore.actions.success,
              {
                title: '#DELIVERY_NOTE.MESSAGES.OVERVIEW_SCAN_SUCCESS.TITLE',
                message: '#DELIVERY_NOTE.MESSAGES.OVERVIEW_SCAN_SUCCESS.MESSAGE',
              }
            );
          } catch (e) {
            console.log('Error: ', e);

            if (e && e instanceof Error && e?.message === 'InputScanner: canceled scan') {
              return;
            }

            if (e && (e as any)?.status === 420) {
              await this.store.dispatch(
                SOFTLINE_FEATURE_MESSAGE_BAR,
                MessageBarStore.actions.error,
                {
                  title: 'Der Lieferschein wurde bereits ausgeliefert!',
                }
              );
              return;
            }

            showRequestErrors(this.store, e as Error);
          }
        },
      },
    ];
  }

  onSearchInputChange(newValue: string): void {
    this.searchInput$.next(newValue);
  }

  onChangeListType({
    value,
  }: {
    value: DeliveryNote['type']['type'];
    label: string;
  }): void {
    this.filterType$.next(value);
  }

  dateChanged(newDate: string): void {
    this.store.commit(
      SOFTLINE_FEATURE_DELIVERY_NOTE,
      DeliveryNotesStore.mutations.setViewedDate,
      newDate
    );
  }

  async showDetails(id: number) {
    let lockedByAnwender: string | undefined;

    try {
      const result = await this.store.dispatch(
        SOFTLINE_FEATURE_DELIVERY_NOTE,
        DeliveryNotesStore.actions.exzess,
        { idlf: id, clear: false }
      );

      if (
        result &&
        result?.success === false &&
        (result?.anwender?.anwendername || result?.anwender?.anwender)
      ) {
        lockedByAnwender = result?.anwender?.anwendername ?? result?.anwender?.anwender;
      }
    } catch (e) {
      console.log('ERROR: ', e);
    } finally {
      if (lockedByAnwender) {
        await this.router.navigate(['lieferscheine', id], {
          queryParams: { lockedByAnwender },
        });
      } else {
        await this.router.navigate(['lieferscheine', id]);
      }
    }
  }

  private filterItemsByType(
    items: readonly DeliveryNote[],
    type: DeliveryNote['type']['type']
  ): DeliveryNote[] {
    if (type === 'MATERIAL') return items.filter((o) => o.type.type === type);

    return items.filter(
      (o) =>
        o.type.type === 'AUSLIEFERUNG' ||
        o.type.type === 'BARVERKAUF' ||
        o?.type?.type === 'KREDITRECHNUNG'
    );
  }
}
